import { useEffect, useState } from 'react';
import { useStore } from '../store.js';

/**
 * Vital-signs dashboard. Always visible at the bottom of the workbench;
 * shows the live metrics an author cares about while editing.
 *
 * Left → right:
 *   file label · lint pulse · embedding coverage · feature count ·
 *   build badge · design voice.
 *
 * The lint pulse is the "heart rate" indicator — green when clean,
 * caution when warnings, crit on errors. It visually pulses on any
 * transition so you notice when a fix lands or a regression appears.
 */
export function StatusBar() {
  const s = useStore();
  const doc = s.doc;

  // Lint vital — animate on transition.
  const lintTier: 'good' | 'caution' | 'crit' | 'neutral' = s.parseError
    ? 'crit'
    : s.errors.length > 0
      ? 'crit'
      : s.warnings.length > 0
        ? 'caution'
        : doc
          ? 'good'
          : 'neutral';
  const [beat, setBeat] = useState(false);
  const [prevTier, setPrevTier] = useState(lintTier);
  useEffect(() => {
    if (lintTier !== prevTier) {
      setBeat(true);
      const id = setTimeout(() => setBeat(false), 1200);
      setPrevTier(lintTier);
      return () => clearTimeout(id);
    }
  }, [lintTier, prevTier]);

  const lintText =
    lintTier === 'crit' && s.parseError ? 'parse error' :
    lintTier === 'crit' ? `${s.errors.length} error${s.errors.length === 1 ? '' : 's'}` :
    lintTier === 'caution' ? `${s.warnings.length} warn${s.warnings.length === 1 ? '' : 's'}` :
    lintTier === 'good' ? 'clean' : '—';

  const lintColor = {
    good:    'text-status-deployed',
    caution: 'text-status-building',
    crit:    'text-status-deprecated',
    neutral: 'text-ink-500',
  }[lintTier];

  const lintDot = {
    good:    'bg-status-deployed',
    caution: 'bg-status-building',
    crit:    'bg-status-deprecated',
    neutral: 'bg-ink-600',
  }[lintTier];

  // Embedding coverage — target = planned, actual = built
  const embCount = s.build.artifact
    ? Object.keys(s.build.artifact.embeddings).length
    : 0;
  const embProgress = s.build.progress
    ? `${s.build.progress.done}/${s.build.progress.total}`
    : null;

  const featureCount = doc?.features?.length ?? 0;
  const flowCount = doc?.flows?.length ?? 0;

  const voice = doc?.design && typeof doc.design === 'object' && 'voice' in doc.design
    ? String((doc.design as { voice?: unknown }).voice ?? '')
    : '';

  return (
    <footer
      role="status"
      className="flex h-7 items-center gap-4 border-t border-ink-800 bg-ink-950 px-3 text-[11px] text-ink-400"
    >
      <span className="mono text-ink-500 truncate max-w-[32ch]">
        {s.fileName ?? '(empty)'}
      </span>

      <span className="flex-1" />

      {/* Lint vital — the heart-rate indicator */}
      <span className={`flex items-center gap-1.5 mono ${beat ? 'tick-pulse' : ''}`}>
        <span className={`inline-block h-1.5 w-1.5 rounded-full ${lintDot}`} />
        <span className={lintColor}>lint · {lintText}</span>
      </span>

      {/* Embedding coverage */}
      {doc && (
        <span className="flex items-center gap-1.5 mono">
          {s.build.status === 'running' && embProgress ? (
            <span className="text-accent-500">embed · {embProgress}</span>
          ) : embCount > 0 ? (
            <span className="text-ink-300">embed · {embCount}</span>
          ) : (
            <span className="text-ink-600">embed · —</span>
          )}
        </span>
      )}

      {/* Feature / flow count */}
      {doc && (
        <span className="mono text-ink-500">
          f·{featureCount} / fl·{flowCount}
        </span>
      )}

      {/* Kind + version */}
      {doc && (
        <span className="mono text-ink-500">
          {doc.remedy?.id} · {doc.kind} · v{doc.version}
        </span>
      )}

      {voice && (
        <span
          className="mono max-w-[28ch] truncate text-ink-600"
          title={voice}
        >
          voice: {voice}
        </span>
      )}
    </footer>
  );
}
